import { Spin } from "antd";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query";

import {
  PRME_ORGANIZATION_ID,
  Roles,
  SAMPLE_SURVEY_PATH_PARAM_VALUE,
} from "@/shared/utils/constants";
import { useSurveyPermissions } from "@/features/survey/hooks/useSurveyPermissions";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import { useGetSurveyByIdQuery } from "@/redux/service";
import SurveyManagement from "@/features/survey/management";
import SurveySummary from "@/features/survey/summary";
import { isSurveyAdminResponse } from "@/redux/survey/typeGuards";

const SurveyDetails = () => {
  const { surveyId } = useParams();
  const location = useLocation();

  const isSample = location.pathname.includes(SAMPLE_SURVEY_PATH_PARAM_VALUE);
  const { data: survey, isError } = useGetSurveyByIdQuery(
    surveyId
      ? {
          surveyId,
          filters: {
            organization: isSample ? PRME_ORGANIZATION_ID : undefined,
          },
        }
      : skipToken,
  );
  const currentUser = useSelector(selectCurrentUser);
  const {
    canView,
    canEdit,
    isLoading: isLoadingPermissions,
  } = useSurveyPermissions(survey, currentUser);

  if (!survey || isLoadingPermissions || !currentUser) {
    return (
      <div className="d-flex justify-content-center align-items-center h-100">
        <Spin size="large" />
      </div>
    );
  }

  if (isError || !canView) {
    return <Navigate to="/" />;
  }

  if (currentUser.role === Roles.ADMIN.value) {
    if (!isSurveyAdminResponse(survey)) {
      return <Navigate to="/" />;
    }
    return <SurveyManagement survey={survey} />;
  }

  if (canEdit) {
    return <SurveySummary survey={survey} />;
  }

  return <Navigate to={`/reports/${survey.id}/overview`} />;
};

export default SurveyDetails;
